import { FormGroup } from '@ksyos/design-system';
import { getServiceLabel } from '@common/customControls/getServiceLabel';
import { ProcessEntity } from '@common/enums/processEntity';
import { getDisplayName } from '@common/utils/getDisplayName';
import type { ICustomControlRequestReadProps } from '../../interfaces/customControlRequestReadProps';
import { getSelectedLanguage } from '../../../../../translate';
import { RequestReadButtons } from '../../../../elements/carePathButtons/RequestReadButtons';
import { FormDivider, FormParagraph, UserTextParagraph } from '../../../../elements/common';
import { FormLabel } from '../../../../elements/forms/FormLabel';
import { HorizontalFormGroupRead } from '../../../../elements/forms/horizontalFormGroupRead/HorizontalFormGroupRead';
import { InputContainer } from '../../../../elements/forms/InputContainer';
import { NullableFormattedDate } from '../../../../elements/NullableFormattedDate';
import { ProcessContainer, RequestAssetThumbnails } from '../../../episodes/common';
import { ProcessItemFooter } from '../../../episodes/ProcessItemFooter';
import { ProcessItemHeader } from '../../../episodes/ProcessItemHeader';
import { ProfessionalSummaryDataRead } from '../../../professionalSummaryData/ProfessionalSummaryDataRead';
import { HideRequestedExaminationInTaskListAndHomeCheckbox } from '../../../requests/HideRequestedExaminationInTaskListAndHomeCheckbox';
import { CustomControlCollapseBody } from '../../utils/v1/CustomControlCollapseBody';
import { MedicationsRead } from '../../utils/v1/medications/MedicationsRead';
import { ProfileImages } from '../../utils/v1/ProfileImages';
import { TxcsMedicationsList } from '../../utils/v1/TxcsMedicationsList';
import { TfoV1FollowingSteps } from './TfoV1FollowingSteps';

export function TfoV1RequestRead(props: ICustomControlRequestReadProps) {
    const customControlData = props.request.customControlData;
    const translate = getSelectedLanguage();

    return (
        <ProcessContainer isActiveActivity={props.itemState.initialIsOpen}>
            <ProcessItemHeader
                date={props.process.dateSent || (props.request && props.request.dateSent)}
                isExaminationRequest
                isFirstItem={props.isFirstItem}
                isFirstProcess={props.itemState.isFirstProcess}
                isItemOpen={props.itemState.isOpen}
                onToggle={async (isOpened) =>
                    await props.updateItemState({
                        ...props.itemState,
                        isOpen: isOpened || props.itemState.initialIsOpen === true,
                    })
                }
                organizationName={props.process.requesterOrganization?.name}
                practitionerName={
                    props.process.requesterPractitioner
                        ? getDisplayName(props.process.requesterPractitioner)
                        : undefined
                }
                process={props.process}
                title={getServiceLabel(props.process.code, 'request', props.process.name)}
            />

            {(props.request.customControlData.files ?? []).length > 0 && (
                <FormGroup label={translate.request.referralLetter}>
                    <RequestAssetThumbnails
                        assets={props.request.customControlData.files}
                        requestId={props.request.id}
                    />
                </FormGroup>
            )}

            <CustomControlCollapseBody isOpen={props.itemState.isOpen}>
                <ProfileImages request={props.request} />

                {props.request.customControlData.hba1c && (
                    <HorizontalFormGroupRead>
                        <FormLabel>HbA1c</FormLabel>
                        <InputContainer>
                            <UserTextParagraph>{props.request.customControlData.hba1c + ' mmol/mol'}</UserTextParagraph>
                        </InputContainer>
                    </HorizontalFormGroupRead>
                )}

                {props.request.customControlData.bloeddruk_systolisch && (
                    <HorizontalFormGroupRead>
                        <FormLabel>Bloeddruk systolisch</FormLabel>
                        <InputContainer>
                            <UserTextParagraph>
                                {props.request.customControlData.bloeddruk_systolisch + ' mmHg'}
                            </UserTextParagraph>
                        </InputContainer>
                    </HorizontalFormGroupRead>
                )}

                {props.request.customControlData.bloeddruk_diastolisch && (
                    <HorizontalFormGroupRead>
                        <FormLabel>Bloeddruk diastolisch</FormLabel>
                        <InputContainer>
                            <UserTextParagraph>
                                {props.request.customControlData.bloeddruk_diastolisch + ' mmHg'}
                            </UserTextParagraph>
                        </InputContainer>
                    </HorizontalFormGroupRead>
                )}

                {props.request.customControlData.diabetes_sinds && (
                    <HorizontalFormGroupRead>
                        <FormLabel>Diabetes sinds</FormLabel>
                        <InputContainer>
                            <FormParagraph bold>
                                <NullableFormattedDate value={props.request.customControlData.diabetes_sinds} />
                            </FormParagraph>
                        </InputContainer>
                    </HorizontalFormGroupRead>
                )}

                <FormDivider />

                {customControlData.oculaire_voorgeschiedenis && (
                    <HorizontalFormGroupRead>
                        <FormLabel>Oculaire voorgeschiedenis</FormLabel>
                        <InputContainer>
                            <UserTextParagraph>{customControlData.oculaire_voorgeschiedenis}</UserTextParagraph>
                        </InputContainer>
                    </HorizontalFormGroupRead>
                )}

                {customControlData.andere_dm_complicaties && (
                    <HorizontalFormGroupRead>
                        <FormLabel>Andere DM complicaties</FormLabel>
                        <InputContainer>
                            <FormParagraph bold>{customControlData.andere_dm_complicaties}</FormParagraph>
                        </InputContainer>
                    </HorizontalFormGroupRead>
                )}

                {customControlData.toelichting && (
                    <HorizontalFormGroupRead>
                        <FormLabel>Toelichting</FormLabel>
                        <InputContainer>
                            <UserTextParagraph>{customControlData.toelichting}</UserTextParagraph>
                        </InputContainer>
                    </HorizontalFormGroupRead>
                )}
                <MedicationsRead
                    entity={ProcessEntity.request}
                    entityId={props.request.id}
                    patientUsesMedication={props.request.patientUsesMedication}
                />
                <ProfessionalSummaryDataRead request={props.request} />
                <TxcsMedicationsList requestId={props.request.id} />
                <TfoV1FollowingSteps />
                <RequestReadButtons {...props} />
                <HideRequestedExaminationInTaskListAndHomeCheckbox
                    isLastItem={props.isLastItem}
                    reload={props.reloadRequest}
                    request={props.request}
                />
            </CustomControlCollapseBody>

            {!props.itemState.initialIsOpen && (
                <ProcessItemFooter
                    isItemOpen={props.itemState.isOpen}
                    labelLess={translate.request.hideRequest}
                    labelMore={translate.request.showRequest}
                    onToggle={async (isOpened) => await props.updateItemState({ ...props.itemState, isOpen: isOpened })}
                />
            )}
        </ProcessContainer>
    );
}
